import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ArrowLeftRightIcon, LockKeyholeIcon, SlidersHorizontalIcon } from 'lucide-react';
type Mode = 'adjustment' | 'return';
type Props = {
  mode: Mode;
};
type AdjustmentReason = 'Damaged' | 'Expired' | 'Inventory Count' | 'Lost' | 'Transfer' | 'Other';
const adjustmentReasons: AdjustmentReason[] = ['Damaged', 'Expired', 'Inventory Count', 'Lost', 'Transfer', 'Other'];
export function StockActions({
  mode
}: Props) {
  const {
    currentUser,
    medicines,
    medicineBatches,
    pharmacySettings,
    recordStockMovement
  } = useData();
  const manager = currentUser?.role === 'Pharmacy Manager';
  const [form, setForm] = useState({
    medicineId: '',
    direction: 'in',
    quantity: '',
    batchId: '',
    reason: 'Inventory Count' as AdjustmentReason,
    comment: '',
    reference: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const batches = useMemo(() => medicineBatches.filter(batch => batch.medicineId === form.medicineId).filter(batch => {
    const unexpired = new Date(`${batch.expiryDate}T23:59:59`).getTime() >= Date.now();
    if (mode === 'return' && form.direction === 'in') return unexpired;
    return batch.quantityRemaining > 0 && unexpired;
  }).sort((a, b) => a.expiryDate.localeCompare(b.expiryDate)), [form.direction, form.medicineId, medicineBatches, mode]);
  const requiresBatch = mode === 'return' && form.direction === 'in';
  const locked = mode === 'adjustment' ? !manager : !pharmacySettings.allowMedicineReturns;
  const movementType = mode === 'return' ? form.direction === 'in' ? 'Return In' : 'Return Out' : form.reason === 'Damaged' ? 'Damaged' : form.reason === 'Transfer' ? 'Transfer' : form.reason === 'Expired' ? 'Expired Disposal' : form.direction === 'in' ? 'Adjustment In' : 'Adjustment Out';
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    setError('');
    if (locked) return;
    if (requiresBatch && !form.batchId) {
      setError('Select the original unexpired batch before returning medicine into stock.');
      return;
    }
    const result = await recordStockMovement({
      medicineId: form.medicineId,
      quantityDelta: form.direction === 'in' ? Number(form.quantity) : -Number(form.quantity),
      type: movementType,
      batchId: form.batchId || undefined,
      referenceType: mode === 'return' ? 'Return' : form.reason === 'Transfer' ? 'Transfer' : 'Manual',
      referenceId: form.reference.trim() || undefined,
      reason: mode === 'adjustment' ? form.reason : undefined,
      notes: form.comment
    });
    if (!result.ok) return setError(result.error || 'Unable to record stock activity.');
    setMessage(`${mode === 'adjustment' ? 'Adjustment' : 'Return'} recorded. The live ledger, medicine balance, and batch quantity have been updated.`);
    setForm({
      medicineId: '',
      direction: 'in',
      quantity: '',
      batchId: '',
      reason: 'Inventory Count',
      comment: '',
      reference: ''
    });
  };
  const title = mode === 'adjustment' ? 'Stock Adjustments' : 'Medicine Returns';
  const policyMessage = mode === 'adjustment' ? 'Only a Pharmacy Manager may authorise a stock adjustment. The manager identity, date and time are written to the immutable ledger.' : pharmacySettings.allowMedicineReturns ? `Hospital return policy is active. ${currentUser?.fullName || 'Current pharmacist'} is recorded as the responsible pharmacist.` : 'Hospital return policy is currently disabled. No return can be posted until a Pharmacy Manager enables it.';
  return <div className="max-w-3xl space-y-5" data-id="element-5657" data-mp="r5eirp8sntzx">
      <div data-id="element-5658" data-mp="r5o7zam0lx4k">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-5659" data-mp="r5wk30abcyep">{title}</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-5660" data-mp="w368qdpvp61h">
          {mode === 'adjustment' ? 'Record controlled corrections with complete manager authorisation.' : 'Return stock only under the current hospital returns policy.'}
        </p>
      </div>
      <div className={`flex gap-3 rounded-xl border p-4 text-sm ${locked ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-teal-200 bg-teal-50 text-teal-900'}`} data-id="element-5661" data-mp="8ewovhc86scz">
        <LockKeyholeIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-5662" data-mp="1jtsg02s26maf" />
        <p data-id="element-5663" data-mp="1jta98c7rspo9">{policyMessage}</p>
      </div>
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-5664" data-mp="1oswmglnlm5x2">
        {message && <p role="status" className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-id="element-5665" data-mp="39ae9s8boaqve">
            {message}
          </p>}
        {error && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-5666" data-mp="2zjb75233i3u0">
            {error}
          </p>}
        <fieldset disabled={locked} className="space-y-5 disabled:cursor-not-allowed disabled:opacity-55" data-id="element-5667" data-mp="2qlhcrwow5k9h">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-5668" data-mp="2lk1u2ba6rpu9">
            <Select label="Medicine" required value={form.medicineId} onChange={e => setForm({
            ...form,
            medicineId: e.target.value,
            batchId: ''
          })} options={[{
            value: '',
            label: 'Select medicine'
          }, ...medicines.map(medicine => ({
            value: medicine.id,
            label: `${medicine.name} · ${medicine.currentQuantity} on hand`
          }))]} data-id="element-5669" data-mp="2gnbb9g6zyg55" />
            <Select label="Direction" required value={form.direction} onChange={e => setForm({
            ...form,
            direction: e.target.value
          })} options={[{
            value: 'in',
            label: mode === 'return' ? 'Approved return into stock' : 'Increase stock'
          }, {
            value: 'out',
            label: mode === 'return' ? 'Return to supplier / issue out' : 'Decrease stock'
          }]} data-id="element-5670" data-mp="1zco87vt80buv" />
            <Input label="Quantity" required type="number" min="1" value={form.quantity} onChange={e => setForm({
            ...form,
            quantity: e.target.value
          })} data-id="element-5671" data-mp="u5of55ehqft9" />
            <Select label={requiresBatch ? 'Original unexpired batch' : 'Batch'} required={requiresBatch} value={form.batchId} onChange={e => setForm({
            ...form,
            batchId: e.target.value
          })} options={[{
            value: '',
            label: mode === 'return' && form.direction === 'in' ? 'Select original unexpired batch' : form.direction === 'out' ? 'Use FEFO eligible batch' : 'Create tracked adjustment batch'
          }, ...batches.map(batch => ({
            value: batch.id,
            label: `${batch.batchNumber} · ${batch.quantityRemaining} available · exp ${batch.expiryDate}`
          }))]} data-id="element-5672" data-mp="gfb6f0tryx2v" />
            {mode === 'adjustment' && <Select label="Adjustment reason" required value={form.reason} onChange={e => setForm({
            ...form,
            reason: e.target.value as AdjustmentReason
          })} options={adjustmentReasons.map(reason => ({
            value: reason,
            label: reason
          }))} data-id="element-5673" data-mp="3m92z5pkr2enh" />}
            {mode === 'return' && <Input label="Return reference" value={form.reference} onChange={e => setForm({
            ...form,
            reference: e.target.value
          })} placeholder="Patient, supplier or return reference" data-id="element-5674" data-mp="drg6xh0y7srk" />}
          </div>
          <label className="block text-sm font-medium text-slate-700" data-id="element-5675" data-mp="1ke58vomgc3be">
            {mode === 'adjustment' ? 'Mandatory comment' : 'Return comment'}
            <textarea required value={form.comment} onChange={e => setForm({
            ...form,
            comment: e.target.value
          })} rows={3} className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" placeholder="Explain this transaction for the permanent audit trail" data-id="element-5676" data-mp="2d0hk8obictzj" />
          </label>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-id="element-5677" data-mp="1k06g2zszlqcg">
            <span className="font-semibold" data-id="element-5678" data-mp="1f13oah20pgmf">
              {mode === 'adjustment' ? 'Authorised user:' : 'Responsible pharmacist:'}
            </span>{' '}
            {currentUser?.fullName || 'Signed-in Pharmacy user'} ·{' '}
            <span className="font-semibold" data-id="element-5679" data-mp="22vwmcz7yd8w5">Recorded:</span> at submission with
            date and time
          </div>
          <div className="flex justify-end" data-id="element-5680" data-mp="1xxbt5jr4bph7">
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-id="element-5681" data-mp="2cmhqf26eb5mg">
              {mode === 'adjustment' ? <SlidersHorizontalIcon className="mr-2 h-4 w-4" data-id="element-5682" data-mp="27nem5jy8p3fj" /> : <ArrowLeftRightIcon className="mr-2 h-4 w-4" data-id="element-5683" data-mp="2lmwe286u0wt4" />}
              {mode === 'adjustment' ? 'Record authorised adjustment' : 'Record approved return'}
            </Button>
          </div>
        </fieldset>
      </form>
    </div>;
}